import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import Layout from '../components/Layout';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

// ── Skeleton Loading ────────────────────────────────────────────────────────

const SkeletonCard = ({ className = '' }) => (
  <div className={`bg-white rounded-2xl border border-gray-200 p-6 ${className}`}>
    <div className="flex items-center justify-between">
      <div className="space-y-3 flex-1">
        <div className="skeleton h-8 w-20" />
        <div className="skeleton h-4 w-28" />
      </div>
      <div className="skeleton w-12 h-12 rounded-xl" />
    </div>
  </div>
);

const SkeletonChart = () => (
  <div className="bg-white rounded-2xl border border-gray-200 p-6">
    <div className="skeleton h-5 w-40 mb-2" />
    <div className="skeleton h-4 w-56 mb-6" />
    <div className="skeleton h-[280px] w-full rounded-xl" />
  </div>
);

const AdminSkeleton = () => (
  <Layout>
    <div className="space-y-8">
      <section>
        <div className="skeleton h-6 w-24 mb-4" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => <SkeletonCard key={i} />)}
        </div>
      </section>
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SkeletonChart />
        <SkeletonChart />
      </section>
      <section>
        <div className="skeleton h-6 w-32 mb-4" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="skeleton h-24 rounded-2xl" />
          ))}
        </div>
      </section>
    </div>
  </Layout>
);

// ── Main Component ──────────────────────────────────────────────────────────

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [enrollments, setEnrollments] = useState(null);
  const [attendance, setAttendance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [aiOverview, setAiOverview] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, enrollRes, attendRes] = await Promise.all([
          api.get('/api/analytics/dashboard'),
          api.get('/api/analytics/enrollments'),
          api.get('/api/analytics/attendance'),
        ]);
        setStats(statsRes.data);
        setEnrollments(enrollRes.data);
        setAttendance(attendRes.data);
      } catch (err) {
        setError('Failed to load dashboard data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchAIData = async () => {
      try {
        const [usageRes, accountsRes] = await Promise.all([
          api.get('/api/ai/admin/usage-stats'),
          api.get('/api/ai/admin/accounts'),
        ]);
        const activatedCount = (accountsRes.data || []).filter((a) => a.is_active).length;
        setAiOverview({
          activatedStudents: activatedCount,
          totalCalls: usageRes.data.grand_total_calls ?? 0,
          totalCostInr: usageRes.data.estimated_total_cost_inr ?? 0,
        });
      } catch {
        // CareerAI data is non-critical — silently skip if API fails
      }
    };
    fetchAIData();
  }, []);

  if (loading) return <AdminSkeleton />;

  if (error) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-32 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-sm border border-red-200 p-8 max-w-md w-full text-center">
            <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Something went wrong</h2>
            <p className="text-gray-500 mb-6">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2.5 bg-primary text-white rounded-xl hover:bg-primary/90 active:scale-[0.98] transition-all font-medium"
            >
              Retry
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  const fmt = (n) => (n ?? 0).toLocaleString();

  const statCards = [
    { label: 'Total Students', value: fmt(stats.total_students), icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z', gradient: 'from-[#003479] to-[#005792]' },
    { label: 'Total Trainers', value: fmt(stats.total_trainers), icon: 'M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z', gradient: 'from-[#1B9AAA] to-[#005792]' },
    { label: 'Total Courses', value: fmt(stats.total_courses), icon: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253', gradient: 'from-[#20C997] to-[#1B9AAA]' },
    { label: 'Active Batches', value: fmt(stats.total_batches), icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10', gradient: 'from-[#ffb547] to-[#ffd166]' },
    { label: 'Total Classes', value: fmt(stats.total_classes), icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z', gradient: 'from-[#13263c] to-[#003479]' },
    { label: 'Upcoming Classes', value: fmt(stats.upcoming_classes_count), icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z', gradient: 'from-[#005792] to-[#1B9AAA]' },
  ];

  const enrollmentChartData = (enrollments.enrollments_by_month || []).map((item) => ({
    month: item.month,
    Enrollments: item.count,
  }));

  const attendanceChartData = (attendance.attendance_by_batch || []).map((item) => ({
    batch: item.batch_name.length > 15 ? item.batch_name.slice(0, 15) + '...' : item.batch_name,
    'Attendance %': item.attendance_rate,
  }));

  const quickActions = [
    { label: 'Create Course', icon: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253', color: 'bg-primary', route: '/courses' },
    { label: 'Create Batch', icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10', color: 'bg-secondary', route: '/batches' },
    { label: 'Enroll Student', icon: 'M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z', color: 'bg-[#1B9AAA]', route: '/batches' },
    { label: 'Schedule Class', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z', color: 'bg-[#ffb547]', route: '/classes' },
  ];

  const navLinks = [
    { label: 'User Management', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z', route: '/users' },
    { label: 'Course Management', icon: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253', route: '/courses' },
    { label: 'Batch Management', icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10', route: '/batches' },
    { label: 'Performance Reports', icon: 'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z', route: '/reports' },
  ];

  return (
    <Layout>
      <div className="space-y-8 animate-fade-in">
        {/* Stats Grid */}
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Overview</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {statCards.map((card, i) => (
              <div
                key={card.label}
                style={{ animationDelay: `${i * 60}ms` }}
                className={`animate-fade-in opacity-0 bg-gradient-to-br ${card.gradient} rounded-2xl p-6 text-white shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all duration-200 cursor-default`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-3xl font-bold">{card.value}</p>
                    <p className="text-sm opacity-90 mt-1">{card.label}</p>
                  </div>
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={card.icon} />
                    </svg>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Charts */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Enrollment Trends</h3>
            <p className="text-sm text-gray-500 mb-6">Monthly enrollments over the last 6 months</p>
            {enrollmentChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={enrollmentChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#9ca3af" />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} stroke="#9ca3af" />
                  <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }} />
                  <Legend />
                  <Line type="monotone" dataKey="Enrollments" stroke="#20C997" strokeWidth={3} dot={{ r: 5, fill: '#20C997' }} activeDot={{ r: 7 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[280px] flex flex-col items-center justify-center text-gray-400">
                <svg className="w-12 h-12 mb-3 text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                </svg>
                <p className="text-sm font-medium">No enrollment data yet</p>
                <p className="text-xs mt-1">Data will appear once students enroll</p>
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Attendance Rates</h3>
            <p className="text-sm text-gray-500 mb-6">Attendance percentage by batch</p>
            {attendanceChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={attendanceChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="batch" tick={{ fontSize: 11 }} stroke="#9ca3af" />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} stroke="#9ca3af" unit="%" />
                  <Tooltip formatter={(value) => [`${value}%`, 'Attendance']} contentStyle={{ borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }} />
                  <Legend />
                  <Bar dataKey="Attendance %" fill="#003479" radius={[8, 8, 0, 0]} maxBarSize={50} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[280px] flex flex-col items-center justify-center text-gray-400">
                <svg className="w-12 h-12 mb-3 text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <p className="text-sm font-medium">No attendance data yet</p>
                <p className="text-xs mt-1">Records will populate after classes</p>
              </div>
            )}
          </div>
        </section>

        {/* Quick Actions */}
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {quickActions.map((action) => (
              <button
                key={action.label}
                onClick={() => navigate(action.route)}
                className={`${action.color} text-white rounded-2xl p-6 flex flex-col items-center gap-3 hover:scale-[1.03] active:scale-[0.98] transition-all duration-200 shadow-sm hover:shadow-lg`}
              >
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={action.icon} />
                  </svg>
                </div>
                <span className="font-semibold text-sm">{action.label}</span>
              </button>
            ))}
          </div>
        </section>

        {/* Navigation Links */}
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Management</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {navLinks.map((link) => (
              <button
                key={link.label}
                onClick={() => navigate(link.route)}
                className="bg-white border border-gray-200 rounded-2xl p-6 flex flex-col items-center gap-3 hover:border-primary hover:shadow-md hover:scale-[1.02] transition-all duration-200 group"
              >
                <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center group-hover:bg-primary/10 transition-colors duration-200">
                  <svg className="w-6 h-6 text-gray-500 group-hover:text-primary transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={link.icon} />
                  </svg>
                </div>
                <span className="font-medium text-sm text-gray-700 group-hover:text-primary transition-colors duration-200">{link.label}</span>
              </button>
            ))}
          </div>
        </section>

        {/* CareerAI Overview */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">CareerAI Overview</h2>
            <button
              onClick={() => navigate('/admin/careerai')}
              className="flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
            >
              Manage CareerAI
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              {
                label: 'Activated Students',
                value: aiOverview ? fmt(aiOverview.activatedStudents) : '—',
                icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z',
                gradient: 'from-[#20C997] to-[#1B9AAA]',
              },
              {
                label: 'Total AI Questions',
                value: aiOverview ? fmt(aiOverview.totalCalls) : '—',
                icon: 'M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z',
                gradient: 'from-[#003479] to-[#005792]',
              },
              {
                label: 'Est. Total Cost',
                value: aiOverview ? `₹${aiOverview.totalCostInr.toFixed(2)}` : '—',
                icon: 'M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z',
                gradient: 'from-[#1B9AAA] to-[#003479]',
              },
            ].map((card) => (
              <div
                key={card.label}
                className={`bg-gradient-to-br ${card.gradient} rounded-2xl p-6 text-white shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all duration-200 cursor-default`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-3xl font-bold">{card.value}</p>
                    <p className="text-sm opacity-90 mt-1">{card.label}</p>
                  </div>
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={card.icon} />
                    </svg>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default AdminDashboard;
